import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";

import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CloseIcon from "@mui/icons-material/Close";
import {
  Box,
  Button,
  IconButton,
  InputAdornment,
  LinearProgress,
  Modal,
  Paper,
  Stack,
  TextField,
  Typography
} from "@mui/material";
import { DesktopDatePicker } from "@mui/x-date-pickers";
import { Select } from "components/forms";
import dayjs from "dayjs";
import { FormikProps, useFormik } from "formik";
import { useDateFormatSelect, useDealDefaultCurrencySelect } from "store";
import { useDealActions } from "store/actions-context";
import { useDebtActions } from "store/actions-context";
import { useDealIdSelector } from "store/selectors/deal-selectors/deal-selectors";
import { DebtSegment, MeasureDataDraft } from "store/types";
import { defaultFacilityValues, getEndOfPeriod } from "utils/debt";
import toIsoMidday from "utils/to-iso-midday";
import { useActionHook } from "hooks/shared-hooks/use-action-hook";
import { useMeasureDrawer } from "hooks/debt-hooks/use-measure-drawer";
import { useCreateManyMeasureDataSelect } from "store";
import { MeasureDataGroup, MeasureDataRound } from "components/Measure-data/Groups-form/GroupsFormContext";

import { FacilityTermsDrawerValues } from "../FacilityTermsDrawerValues";
import { FacilityWizardFeesSection } from "./FacilityWizardFeesSection";
import { FloatTypeSelect } from "../form-fields/FloatTypeSelect";
import { LoanAmount } from "../form-fields/LoanAmount";
import { PaymentTerm, getPaymentTermLabel } from "../form-fields/PaymentTerm";
import { RateFixingTypeSelect } from "../form-fields/RateFixingTypeSelect";
import { ReferenceIndexSelect } from "../form-fields/ReferenceIndexSelect";
import { FormValues } from "../schemas";
import { ACCRUAL_OPTIONS, FREQ_LABELS, FREQUENCY_OPTIONS, STEP_META } from "./constants";
import { OptionCard } from "./OptionCard";
import { QuestionSlot } from "./QuestionSlot";
import { SummaryRow, SummarySection } from "./SummaryRow";
import { FacilityCreationWizardProps, InterestType, WithdrawalValues } from "./types";

// ── Main component ────────────────────────────────────────────────────────────

export const FacilityCreationWizard: React.FC<FacilityCreationWizardProps> = ({
  open,
  onClose,
  onApply,
  onCreateWithdrawal,
  loading
}) => {
  const { t } = useTranslation("debt");
  const currency = useDealDefaultCurrencySelect() || "USD";
  const dateFormat = useDateFormatSelect();
  const { getDebtIndexValueAction } = useDebtActions();
  const dealId = useDealIdSelector() ?? 0;
  const { createManyMeasureDataAction, saveMeasureDataDraftAction } = useDealActions();
  const { action: createManyMeasureData, loading: measureDataLoading } = useActionHook(createManyMeasureDataAction);
  const createMeasureDataMeta = useCreateManyMeasureDataSelect();

  // ── Wizard-level state ────────────────────────────────────────────────────

  const [step, setStep] = useState(0);
  const [submitting, setSubmitting] = useState(false);
  const [facilityCreated, setFacilityCreated] = useState(false);
  const [serverError, setServerError] = useState<string[] | null>(null);
  const [wizardSegments, setWizardSegments] = useState<FormValues[] | null>(null);
  const [facilitySnapshot, setFacilitySnapshot] = useState<FormValues | null>(null);

  const { rounds, roundsLoading } = useMeasureDrawer([dealId, ...(wizardSegments ?? []).map(segment => segment.deal_id).filter(Boolean)]);

  // Step 0 — explicit "debt type" choice (is_facility has no obvious default from UX POV)
  const [debtTypeChosen, setDebtTypeChosen] = useState(false);

  // Step 2 — which interest types apply
  const [interestType, setInterestType] = useState<InterestType | null>(null);

  // Step 1 — whether principal is index-linked
  const [principalLinked, setPrincipalLinked] = useState<boolean | null>(null);

  // Step 4 — whether there are fees
  const [hasFees, setHasFees] = useState<boolean | null>(null);

  // Sequential reveal tracker: records which fields/concepts the user has filled in
  const [ft, setFt] = useState<Set<string>>(new Set());
  const touch = useCallback(
    (name: string) =>
      setFt((prev) => {
        if (prev.has(name)) return prev;
        const next = new Set(prev);
        next.add(name);
        return next;
      }),
    []
  );

  // Withdrawal data (separate form, simpler shape)
  const [wv, setWv] = useState<WithdrawalValues>({
    deal_title: "",
    loan_amount: 0,
    start_date: dayjs().format(),
    loan_first_status_date: null,
    principal_first_payment_date: null,
    interest_first_payment_date: null,
    pik_first_capitalization_date: null
  });

  // Main formik instance
  const formik = useFormik<FormValues>({
    initialValues: { ...defaultFacilityValues },
    onSubmit: () => {}
  });
  const { values, setFieldValue } = formik;

  // ── Reset on close ────────────────────────────────────────────────────────

  useEffect(() => {
    if (!open) {
      formik.resetForm({ values: { ...defaultFacilityValues } });
      setStep(0);
      setSubmitting(false);
      setFacilityCreated(false);
      setDebtTypeChosen(false);
      setInterestType(null);
      setPrincipalLinked(null);
      setHasFees(null);
      setFt(new Set());
      setWizardSegments(null);
      setFacilitySnapshot(null);
      setWv({
        deal_title: "",
        loan_amount: 0,
        start_date: dayjs().format(),
        loan_first_status_date: null,
        principal_first_payment_date: null,
        interest_first_payment_date: null,
        pik_first_capitalization_date: null
      });
    }
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Navigation ────────────────────────────────────────────────────────────

  const hasRegularInterest = ["regular", "both"].includes(interestType ?? "");
  const hasPIK = ["pik", "both"].includes(interestType ?? "");
  const hasPrincipalSchedule = ["fixed_principal", "fixed_total"].includes(values.payment_term);

  const isSimpleLoan = values.is_facility && values.is_withdrawal;

  const activeSteps = useMemo(() => {
    const active = [0];
    if (hasPrincipalSchedule) active.push(1);
    active.push(2); // Interest Type
    if (hasRegularInterest) active.push(3); // Interest Rate Terms
    if (hasPIK) active.push(4); // PIK Interest Terms
    if (values.is_facility) active.push(5); // Fees
    active.push(6); // Review & Confirm
    if (!isSimpleLoan && values.is_facility) {
      active.push(7); // First Drawdown
      active.push(8); // Drawdown Review
    }
    active.push(9); // Done
    return active;
  }, [hasPrincipalSchedule, hasRegularInterest, hasPIK, values.is_facility, isSimpleLoan]);
  const currentStepIndex = activeSteps.indexOf(step);
  const totalSteps = activeSteps.length;
  const progressPct = totalSteps > 1 ? (currentStepIndex / (totalSteps - 1)) * 100 : 0;

  const goNext = () => {
    const next = activeSteps[currentStepIndex + 1];
    if (next === undefined) return;
    // Auto-fill first payment dates for simple loans when entering a step with default frequency already set
    if (isSimpleLoan && values.start_date) {
      const start = dayjs(values.start_date);
      if (next === 1 && hasPrincipalSchedule && !values.principal_first_payment_date)
        setFieldValue("principal_first_payment_date", getEndOfPeriod(start, values.principal_frequency));
      if (next === 3 && !values.interest_first_payment_date)
        setFieldValue("interest_first_payment_date", getEndOfPeriod(start, values.interest_frequency));
      if (next === 4 && values.pik_capitalization_frequency && !values.pik_first_capitalization_date)
        setFieldValue("pik_first_capitalization_date", getEndOfPeriod(start, values.pik_capitalization_frequency));
    }
    // Default drawdown date + auto-fill first payment dates when entering step 7
    if (next === 7 && values.start_date) {
      const start = dayjs(values.start_date);
      setWv((prev) => ({
        ...prev,
        start_date: values.start_date,
        ...(values.loan_status_frequency
          ? { loan_first_status_date: getEndOfPeriod(start, values.loan_status_frequency) }
          : {}),
        ...(hasPrincipalSchedule && values.principal_frequency
          ? { principal_first_payment_date: getEndOfPeriod(start, values.principal_frequency) }
          : {}),
        ...(hasRegularInterest && values.interest_frequency
          ? { interest_first_payment_date: getEndOfPeriod(start, values.interest_frequency) }
          : {}),
        ...(hasPIK && values.pik_capitalization_frequency
          ? { pik_first_capitalization_date: getEndOfPeriod(start, values.pik_capitalization_frequency) }
          : {})
      }));
    }
    setStep(next);
  };
  const goBack = () => {
    const prev = activeSteps[currentStepIndex - 1];
    if (prev !== undefined) {
      setStep(prev);
      setServerError(null);
    }
  };

  // ── Index value fetching ──────────────────────────────────────────────────

  const fetchIndexValue = async (
    index: string,
    field: "interest_index_value" | "principal_index_value" | "pik_index_value"
  ) => {
    try {
      const val = await getDebtIndexValueAction(index, dayjs().format("YYYY-MM-DD"));
      setFieldValue(field, val);
    } catch (_) {
      /* ignore */
    }
  };

  // ── Segment builders ──────────────────────────────────────────────────────

  const buildFacilitySegment = (): DebtSegment => {
    const {
      interest_index_value: _1,
      principal_index_value: _2,
      pik_index_value: _3,
      custom_payments_file: _4,
      fees: _5,
      ...rest
    } = values;

    const seg: DebtSegment = { ...rest, id: 0, deal_id: 0 };

    if (interestType === "pik" || interestType === "none") {
      seg.base_rate = null;
      seg.interest_first_payment_date = null;
    }
    if (interestType === "regular" || interestType === "none") {
      seg.pik_base_rate = null;
      seg.pik_first_capitalization_date = null;
    }

    return seg;
  };

  // ── Submit handlers ───────────────────────────────────────────────────────

  const extractErrors = (err: any): string[] => {
    const errors: Record<string, string[]> = err?.errors || {};
    const items = Object.values(errors).flat();
    return items.length ? items : [err?.message || "An unexpected error occurred"];
  };

  const handleFacilityConfirm = async () => {
    setSubmitting(true);
    setServerError(null);
    try {
      const seg = buildFacilitySegment();
      const fees = hasFees ? values.fees : undefined;
      await onApply([seg], undefined, fees);
      setFacilityCreated(true);
      if (isSimpleLoan) {
        setWizardSegments([{ ...values, deal_id: dealId }]);
      } else {
        setFacilitySnapshot({ ...values, deal_id: dealId });
        goNext();
      }
    } catch (err) {
      const msgs = extractErrors(err);
      setServerError(msgs);
      toast.error(msgs.join(" "));
    } finally {
      setSubmitting(false);
    }
  };

  const handleWithdrawalConfirm = async () => {
    setSubmitting(true);
    setServerError(null);
    try {
      const withdrawalSeg: DebtSegment = {
        ...buildFacilitySegment(),
        id: 0,
        deal_id: 0,
        deal_title: wv.deal_title,
        loan_amount: wv.loan_amount,
        start_date: wv.start_date,
        loan_first_status_date: wv.loan_first_status_date,
        principal_first_payment_date: wv.principal_first_payment_date,
        interest_first_payment_date: wv.interest_first_payment_date,
        pik_first_capitalization_date: wv.pik_first_capitalization_date,
        is_facility: false,
        is_withdrawal: true
      };
      const result = await onCreateWithdrawal(withdrawalSeg);
      const createdDealId = result?.data?.deal_id ?? 0;
      const withdrawalValues: FormValues = { ...values, is_facility: false, is_withdrawal: true, loan_amount: wv.loan_amount, start_date: wv.start_date, deal_id: createdDealId };
      setWizardSegments(facilitySnapshot ? [facilitySnapshot, withdrawalValues] : [withdrawalValues]);
    } catch (err) {
      const msgs = extractErrors(err);
      setServerError(msgs);
      toast.error(msgs.join(" "));
    } finally {
      setSubmitting(false);
    }
  };

  const handleSaveMeasureData = async (data: { groups: MeasureDataGroup[]; roundsByDeals: Record<number, MeasureDataRound[]> }) => {
    await createManyMeasureData(data);
    setWizardSegments(null);
    goNext();
  };

  const handleSaveMeasureDataAsDraft = async (data: MeasureDataDraft["data"]) => {
    await saveMeasureDataDraftAction({
      deal_id: dealId,
      data,
      ...(createMeasureDataMeta?.draftId && { id: createMeasureDataMeta.draftId })
    });
    setWizardSegments(null);
    goNext();
  };

  // ── Formatters used in review screens ────────────────────────────────────

  const fmtDate = (d: string | null | undefined) =>
    d ? dayjs(d).format(dateFormat || "YYYY-MM-DD") : "—";
  const fmtRate = (r: number | null | undefined) => (r != null ? `${r}%` : null);
  const fmtFreq = (f: string | null | undefined) => (f ? FREQ_LABELS[f] ?? f : null);
  const fmtFloat = (f: string) => ({ fixed: "Fixed", linked: "Linked", spread: "Spread" }[f] ?? f);

  // ── Step 0: Debt Configuration ────────────────────────────────────────────

  const renderStep0 = () => {
    const showDates = debtTypeChosen && values.loan_amount > 0;
    return (
      <Box>
        <QuestionSlot visible label="What type of debt are you creating?">
          <Stack spacing={2}>
            <OptionCard
              value="loan"
              selected={debtTypeChosen && values.is_withdrawal}
              onSelect={() => {
                setFieldValue("is_facility", true);
                setFieldValue("is_withdrawal", true);
                setFieldValue("overriding_parent", false);
                setDebtTypeChosen(true);
              }}
              title="Simple Loan"
              subtitle="A single loan with defined repayment terms — no drawdown flexibility"
            />
            <OptionCard
              value="facility"
              selected={debtTypeChosen && values.is_facility && !values.is_withdrawal}
              onSelect={() => {
                setFieldValue("is_facility", true);
                setFieldValue("is_withdrawal", false);
                setDebtTypeChosen(true);
              }}
              title="Credit Facility with Drawdowns"
              subtitle="A revolving or term credit facility that supports multiple drawdowns"
            />
          </Stack>
        </QuestionSlot>

        <QuestionSlot visible={debtTypeChosen} label="What are the payment terms?">
          <PaymentTerm formik={formik} showCustomIndexationOptions={false} />
        </QuestionSlot>

        <QuestionSlot
          visible={debtTypeChosen}
          label={values.is_facility ? "What is the total commitment amount?" : "What is the loan amount?"}
        >
          <LoanAmount
            formik={formik}
            currency={currency}
            label={values.is_facility ? "Commitment Amount" : "Loan Amount"}
          />
        </QuestionSlot>

        <QuestionSlot visible={showDates} label="When does this loan start?">
          <DesktopDatePicker
            format={dateFormat}
            views={["year", "month", "day"]}
            label="Start Date"
            value={values.start_date ? dayjs(values.start_date) : null}
            onChange={(v) => {
              if (v) {
                setFieldValue("start_date", toIsoMidday(v));
                if (isSimpleLoan && values.loan_status_frequency)
                  setFieldValue("loan_first_status_date", getEndOfPeriod(v, values.loan_status_frequency));
                touch("start_date");
              }
            }}
            sx={{ width: "100%" }}
          />
        </QuestionSlot>

        <QuestionSlot visible={showDates} label="When does this loan reach its final maturity?">
          <DesktopDatePicker
            format={dateFormat}
            views={["year", "month", "day"]}
            label="Maturity Date"
            minDate={values.start_date ? dayjs(values.start_date) : undefined}
            value={values.maturity_date ? dayjs(values.maturity_date) : null}
            onChange={(v) => {
              if (v) {
                setFieldValue("maturity_date", toIsoMidday(v));
                touch("maturity_date");
              }
            }}            
            sx={{ width: "100%" }}
          />
        </QuestionSlot>

        <QuestionSlot visible={showDates} label="How often should the loan report status?">
          <Select
            name="loan_status_frequency"
            label="Status Reporting Frequency"
            variant="outlined"
            fullWidth
            value={values.loan_status_frequency || "quarter"}
            items={FREQUENCY_OPTIONS}
            onChange={(e) => {
              setFieldValue("loan_status_frequency", e.target.value);
              if (isSimpleLoan && values.start_date)
                setFieldValue("loan_first_status_date", getEndOfPeriod(dayjs(values.start_date), e.target.value));
            }}
          />
        </QuestionSlot>

        {isSimpleLoan && (
          <QuestionSlot visible={showDates && !!values.loan_status_frequency} label="First status reporting date">
            <DesktopDatePicker
              format={dateFormat}
              views={["year", "month", "day"]}
              label="First Status Date"
              value={values.loan_first_status_date ? dayjs(values.loan_first_status_date) : null}
              onChange={(v) => setFieldValue("loan_first_status_date", v ? toIsoMidday(v) : null)}
              sx={{ width: "100%" }}
              slotProps={{ textField: { helperText: "Auto-calculated — adjust if needed" } }}
            />
          </QuestionSlot>
        )}
      </Box>
    );
  };

  // ── Step 1: Principal Repayment Setup ─────────────────────────────────────

  const renderStep1 = () => (
    <Box>
      <QuestionSlot visible label="How frequently will principal payments be made?">
        <Select
          name="principal_frequency"
          label="Principal Payment Frequency"
          variant="outlined"
          fullWidth
          value={values.principal_frequency ?? ""}
          items={[{ key: "none", value: "", text: "None" }, ...FREQUENCY_OPTIONS]}
          onChange={(e) => {
            const freq = e.target.value || null;
            setFieldValue("principal_frequency", freq);
            if (isSimpleLoan && freq && values.start_date)
              setFieldValue("principal_first_payment_date", getEndOfPeriod(dayjs(values.start_date), freq));
            touch("principal_frequency");
          }}
        />
      </QuestionSlot>

      <QuestionSlot visible label="Is the principal amount linked to a market index?">
        <Stack spacing={2}>
          <OptionCard
            value="no"
            selected={principalLinked === false}
            onSelect={() => {
              setPrincipalLinked(false);
              setFieldValue("principal_reference_index", null);
              setFieldValue("principal_link_percent", 0);
            }}
            title="No — Fixed principal"
            subtitle="The outstanding principal is not indexed to any market benchmark"
          />
          <OptionCard
            value="yes"
            selected={principalLinked === true}
            onSelect={() => setPrincipalLinked(true)}
            title="Yes — Index-linked principal"
            subtitle="The principal balance adjusts according to a reference index (e.g. Consumer Price Index)"
          />
        </Stack>
      </QuestionSlot>

      <QuestionSlot visible={principalLinked === true} label="Which reference index tracks the principal?">
        <ReferenceIndexSelect
          name="principal_reference_index"
          formik={formik}
          label="Principal Reference Index"
          onIndexChange={(idx) => {
            fetchIndexValue(idx, "principal_index_value");
            touch("principal_index_selected");
          }}
          onIndexClear={() => setFieldValue("principal_link_percent", 0)}
        />
      </QuestionSlot>

      <QuestionSlot
        visible={principalLinked === true && Boolean(values.principal_reference_index)}
        label="What percentage of the index applies to the principal?"
      >
        <TextField
          fullWidth
          type="number"
          label="Index Link Percentage"
          value={values.principal_link_percent || ""}
          onChange={(e) => setFieldValue("principal_link_percent", parseFloat(e.target.value) || 0)}
          slotProps={{ input: { endAdornment: <InputAdornment position="end">%</InputAdornment> } }}
        />
      </QuestionSlot>

      {isSimpleLoan && (
        <QuestionSlot visible label="First principal payment date">
          <DesktopDatePicker
            format={dateFormat}
            views={["year", "month", "day"]}
            label="First Principal Payment"
            value={values.principal_first_payment_date ? dayjs(values.principal_first_payment_date) : null}
            onChange={(v) => setFieldValue("principal_first_payment_date", v ? toIsoMidday(v) : null)}
            sx={{ width: "100%" }}
            slotProps={{ textField: { helperText: "Auto-calculated — adjust if needed" } }}
          />
        </QuestionSlot>
      )}
    </Box>
  );

  // ── Step 2: Interest Type ─────────────────────────────────────────────────

  const renderStep2 = () => (
    <Box>
      <QuestionSlot visible label="What type of interest does this debt carry?">
        <Stack spacing={2}>
          {([
            ["regular", "Regular (Cash) Interest", "Periodic cash interest payments on the outstanding principal"],
            ["pik", "PIK — Payment-In-Kind Only", "Interest accrues and capitalizes into principal — no cash payments"],
            ["both", "Both Regular and PIK", "A blended structure with cash interest and an additional PIK component"],
            ["none", "No Interest", "This debt carries no interest; principal repayment only"]
          ] as [InterestType, string, string][]).map(([v, title, subtitle]) => (
            <OptionCard
              key={v}
              value={v}
              selected={interestType === v}
              onSelect={(val) => setInterestType(val as InterestType)}
              title={title}
              subtitle={subtitle}
            />
          ))}
        </Stack>
      </QuestionSlot>
    </Box>
  );

  // ── Step 3: Interest Rate Terms ───────────────────────────────────────────

  const renderStep3 = () => (
    <Box>
      <QuestionSlot
        visible={hasRegularInterest}
        label="What is the base rate and how is it structured?"
      >
        <Stack spacing={3}>
          <TextField
            fullWidth
            type="number"
            label="Base Interest Rate"
            value={values.base_rate ?? ""}
            onChange={(e) => {
              setFieldValue("base_rate", e.target.value === "" ? null : parseFloat(e.target.value));
              touch("base_rate");
            }}
            slotProps={{ input: { endAdornment: <InputAdornment position="end">%</InputAdornment> } }}
          />
          <FloatTypeSelect
            name="float_type"
            formik={formik}
            label="Rate Structure"
            onChange={(e) => {
              formik.handleChange(e);
              if (e.target.value === "fixed") {
                setFieldValue("interest_reference_index", null);
                setFieldValue("interest_index_value", null);
              }
              touch("float_type");
            }}
          />
        </Stack>
      </QuestionSlot>

      <QuestionSlot
        visible={hasRegularInterest && values.float_type !== "fixed"}
        label="Which market index should the interest rate reference?"
      >
        <ReferenceIndexSelect
          name="interest_reference_index"
          formik={formik}
          label="Interest Reference Index"
          onIndexChange={(idx) => fetchIndexValue(idx, "interest_index_value")}
        />
      </QuestionSlot>

      <QuestionSlot
        visible={hasRegularInterest && values.float_type === "spread"}
        label="How should the reference rate be fixed for each period?"
      >
        <RateFixingTypeSelect name="interest_rate_fixing" formik={formik} label="Rate Fixing Convention" />
      </QuestionSlot>

      <QuestionSlot
        visible={hasRegularInterest && values.float_type === "linked"}
        label="What percentage of the index applies to the interest rate?"
      >
        <TextField
          fullWidth
          type="number"
          label="Index Link Percentage"
          value={values.interest_link_percent || ""}
          onChange={(e) => setFieldValue("interest_link_percent", parseFloat(e.target.value) || 0)}
          slotProps={{ input: { endAdornment: <InputAdornment position="end">%</InputAdornment> } }}
        />
      </QuestionSlot>

      <QuestionSlot
        visible={hasRegularInterest && ft.has("base_rate")}
        label="How frequently will interest payments be made?"
      >
        <Select
          name="interest_frequency"
          label="Interest Payment Frequency"
          variant="outlined"
          fullWidth
          value={values.interest_frequency}
          items={FREQUENCY_OPTIONS}
          onChange={(e) => {
            setFieldValue("interest_frequency", e.target.value);
            if (isSimpleLoan && values.start_date)
              setFieldValue("interest_first_payment_date", getEndOfPeriod(dayjs(values.start_date), e.target.value));
            touch("interest_frequency");
          }}
        />
        {isSimpleLoan && values.interest_frequency && (
          <DesktopDatePicker
            format={dateFormat}
            views={["year", "month", "day"]}
            label="First Interest Payment"
            value={values.interest_first_payment_date ? dayjs(values.interest_first_payment_date) : null}
            onChange={(v) => setFieldValue("interest_first_payment_date", v ? toIsoMidday(v) : null)}
            sx={{ width: "100%", mt: 3 }}
            slotProps={{ textField: { helperText: "Auto-calculated — adjust if needed" } }}
          />
        )}
      </QuestionSlot>

      <QuestionSlot
        visible={hasRegularInterest && ft.has("interest_frequency")}
        label="What is the day-count convention?"
      >
        <Select
          name="day_count_convention"
          label="Day-Count Convention"
          variant="outlined"
          fullWidth
          value={values.day_count_convention}
          items={ACCRUAL_OPTIONS}
          onChange={formik.handleChange}
        />
      </QuestionSlot>

      <QuestionSlot
        visible={hasRegularInterest && ft.has("interest_frequency")}
        label="Are there any floor or ceiling rate constraints? (optional)"
      >
        <Stack direction="row" spacing={3}>
          <TextField
            fullWidth
            type="number"
            label="Floor Rate"
            value={values.floor_rate ?? ""}
            onChange={(e) =>
              setFieldValue("floor_rate", e.target.value === "" ? null : parseFloat(e.target.value))
            }
            slotProps={{ input: { endAdornment: <InputAdornment position="end">%</InputAdornment> } }}
          />
          <TextField
            fullWidth
            type="number"
            label="Ceiling Rate"
            value={values.ceiling_rate ?? ""}
            onChange={(e) =>
              setFieldValue("ceiling_rate", e.target.value === "" ? null : parseFloat(e.target.value))
            }
            slotProps={{ input: { endAdornment: <InputAdornment position="end">%</InputAdornment> } }}
          />
        </Stack>
      </QuestionSlot>

      <QuestionSlot
        visible={hasRegularInterest && hasPIK}
        label="Should regular interest accrue on PIK principal as well?"
      >
        <OptionCard
          value="yes"
          selected={!!values.interest_on_pik_principal}
          onSelect={() => setFieldValue("interest_on_pik_principal", true)}
          title="Yes — include PIK principal in interest basis"
          subtitle="Regular interest will be calculated on outstanding principal plus any capitalised PIK principal"
        />
        <OptionCard
          value="no"
          selected={!values.interest_on_pik_principal}
          onSelect={() => setFieldValue("interest_on_pik_principal", false)}
          title="No — interest on original principal only"
          subtitle="Regular interest is calculated on the original outstanding principal, excluding capitalised PIK"
        />
      </QuestionSlot>

      {isSimpleLoan && (
        <QuestionSlot
          visible={hasRegularInterest && values.float_type !== "fixed"}
          label="Interest reference date and day offset"
        >
          <Stack spacing={3}>
            <DesktopDatePicker
              format={dateFormat}
              views={["year", "month", "day"]}
              label="Interest Reference Date"
              value={values.interest_reference_day ? dayjs(values.interest_reference_day) : null}
              onChange={(v) => setFieldValue("interest_reference_day", v ? toIsoMidday(v) : null)}
              sx={{ width: "100%" }}
            />
            <TextField
              fullWidth
              type="number"
              name="interest_reference_day_offset"
              label="Reference Day Offset"
              value={values.interest_reference_day_offset ?? ""}
              onChange={(e) =>
                setFieldValue("interest_reference_day_offset", e.target.value === "" ? null : parseInt(e.target.value, 10))
              }
              slotProps={{ input: { inputProps: { step: 1 } } }}
            />
          </Stack>
        </QuestionSlot>
      )}
    </Box>
  );

  // ── Step 4: PIK Interest Terms ────────────────────────────────────────────

  const renderStep4 = () => (
    <Box>
      <QuestionSlot visible label="What is the PIK base rate and how is it structured?">
        <Stack spacing={3}>
          <TextField
            fullWidth
            type="number"
            label="PIK Base Rate"
            value={values.pik_base_rate ?? ""}
            onChange={(e) => {
              setFieldValue("pik_base_rate", e.target.value === "" ? null : parseFloat(e.target.value));
              touch("pik_base_rate");
            }}
            slotProps={{ input: { endAdornment: <InputAdornment position="end">%</InputAdornment> } }}
          />
          <FloatTypeSelect
            name="pik_float_type"
            formik={formik}
            label="PIK Rate Structure"
            onChange={(e) => {
              formik.handleChange(e);
              if (e.target.value === "fixed") {
                setFieldValue("pik_reference_index", null);
                setFieldValue("pik_index_value", null);
              }
              touch("pik_float_type");
            }}
          />
        </Stack>
      </QuestionSlot>

      <QuestionSlot
        visible={values.pik_float_type !== "fixed"}
        label="Which market index does the PIK rate reference?"
      >
        <ReferenceIndexSelect
          name="pik_reference_index"
          formik={formik}
          label="PIK Reference Index"
          onIndexChange={(idx) => fetchIndexValue(idx, "pik_index_value")}
        />
      </QuestionSlot>

      <QuestionSlot
        visible={values.pik_float_type === "spread"}
        label="How should the PIK reference rate be fixed for each period?"
      >
        <RateFixingTypeSelect name="pik_rate_fixing" formik={formik} label="PIK Rate Fixing Convention" />
      </QuestionSlot>

      <QuestionSlot
        visible={values.pik_float_type === "linked"}
        label="What percentage of the index applies to the PIK rate?"
      >
        <TextField
          fullWidth
          type="number"
          label="PIK Index Link Percentage"
          value={values.pik_link_percent ?? ""}
          onChange={(e) =>
            setFieldValue("pik_link_percent", e.target.value === "" ? null : parseFloat(e.target.value))
          }
          slotProps={{ input: { endAdornment: <InputAdornment position="end">%</InputAdornment> } }}
        />
      </QuestionSlot>

      <QuestionSlot visible={ft.has("pik_base_rate")} label="How often does PIK interest capitalize?">
        <Stack spacing={3}>
          <Select
            name="pik_capitalization_frequency"
            label="PIK Capitalization Frequency"
            variant="outlined"
            fullWidth
            value={values.pik_capitalization_frequency ?? ""}
            items={[{ key: "none", value: "", text: "None" }, ...FREQUENCY_OPTIONS]}
            onChange={(e) => {
              const freq = e.target.value || null;
              setFieldValue("pik_capitalization_frequency", freq);
              if (isSimpleLoan && freq && values.start_date)
                setFieldValue("pik_first_capitalization_date", getEndOfPeriod(dayjs(values.start_date), freq));
              touch("pik_cap_frequency");
            }}
          />
          {isSimpleLoan && values.pik_capitalization_frequency && (
            <DesktopDatePicker
              format={dateFormat}
              views={["year", "month", "day"]}
              label="First PIK Capitalization"
              value={values.pik_first_capitalization_date ? dayjs(values.pik_first_capitalization_date) : null}
              onChange={(v) => setFieldValue("pik_first_capitalization_date", v ? toIsoMidday(v) : null)}
              sx={{ width: "100%" }}
              slotProps={{ textField: { helperText: "Auto-calculated — adjust if needed" } }}
            />
          )}
        </Stack>
      </QuestionSlot>

      {!hasRegularInterest && (
        <QuestionSlot visible={ft.has("pik_cap_frequency")} label="What is the day-count convention?">
          <Select
            name="day_count_convention"
            label="Day-Count Convention"
            variant="outlined"
            fullWidth
            value={values.day_count_convention}
            items={ACCRUAL_OPTIONS}
            onChange={formik.handleChange}
          />
        </QuestionSlot>
      )}

      <QuestionSlot
        visible={ft.has("pik_cap_frequency")}
        label="Are there any PIK floor or ceiling rate constraints? (optional)"
      >
        <Stack direction="row" spacing={3}>
          <TextField
            fullWidth
            type="number"
            label="PIK Floor Rate"
            value={values.pik_floor_rate ?? ""}
            onChange={(e) =>
              setFieldValue("pik_floor_rate", e.target.value === "" ? null : parseFloat(e.target.value))
            }
            slotProps={{ input: { endAdornment: <InputAdornment position="end">%</InputAdornment> } }}
          />
          <TextField
            fullWidth
            type="number"
            label="PIK Ceiling Rate"
            value={values.pik_ceiling_rate ?? ""}
            onChange={(e) =>
              setFieldValue("pik_ceiling_rate", e.target.value === "" ? null : parseFloat(e.target.value))
            }
            slotProps={{ input: { endAdornment: <InputAdornment position="end">%</InputAdornment> } }}
          />
        </Stack>
      </QuestionSlot>

      {isSimpleLoan && (
        <QuestionSlot
          visible={!!values.pik_capitalization_frequency && values.pik_float_type !== "fixed"}
          label="PIK reference date and day offset"
        >
          <Stack spacing={3}>
            <DesktopDatePicker
              format={dateFormat}
              views={["year", "month", "day"]}
              label="PIK Reference Date"
              value={values.pik_reference_day ? dayjs(values.pik_reference_day) : null}
              onChange={(v) => setFieldValue("pik_reference_day", v ? toIsoMidday(v) : null)}
              sx={{ width: "100%" }}
            />
            <TextField
              fullWidth
              type="number"
              name="pik_reference_day_offset"
              label="PIK Reference Day Offset"
              value={values.pik_reference_day_offset ?? ""}
              onChange={(e) =>
                setFieldValue("pik_reference_day_offset", e.target.value === "" ? null : parseInt(e.target.value, 10))
              }
              slotProps={{ input: { inputProps: { step: 1 } } }}
            />
          </Stack>
        </QuestionSlot>
      )}
    </Box>
  );

  // ── Step 5: Arrangement Fees ──────────────────────────────────────────────

  const renderStep5 = () => (
    <Box>
      <QuestionSlot visible label="Are there any arrangement or ongoing fees for this facility?">
        <Stack spacing={2}>
          <OptionCard
            value="no"
            selected={hasFees === false}
            onSelect={() => {
              setHasFees(false);
              setFieldValue("fees", []);
            }}
            title="No fees"
            subtitle="This facility has no associated arrangement fees or credit facility fees"
          />
          <OptionCard
            value="yes"
            selected={hasFees === true}
            onSelect={() => setHasFees(true)}
            title="Yes — add fees"
            subtitle="Include arrangement fees, recurring credit facility fees, or one-time costs"
          />
        </Stack>
      </QuestionSlot>

      <QuestionSlot visible={hasFees === true} label="Define the fees for this facility">
        <FacilityWizardFeesSection formik={formik} />
      </QuestionSlot>
    </Box>
  );

  // ── Step 6: Review & Confirm ─────────────────────────────────────────────

  const renderStep6 = () => (
    <Box>
      <SummarySection title="Debt Structure">
        <SummaryRow
          label="Type"
          value={isSimpleLoan ? "Simple Loan" : "Credit Facility with Drawdowns"}
        />
        <SummaryRow
          label="Payment Terms"
          value={getPaymentTermLabel(values.payment_term, t)}
        />
        <SummaryRow
          label={isSimpleLoan ? "Loan Amount" : "Commitment Amount"}
          value={`${currency} ${values.loan_amount?.toLocaleString()}`}
        />
        <SummaryRow label="Start Date" value={fmtDate(values.start_date)} />
        <SummaryRow label="Maturity Date" value={fmtDate(values.maturity_date)} />
        <SummaryRow label="Status Reporting" value={fmtFreq(values.loan_status_frequency)} />
        {isSimpleLoan && values.loan_first_status_date && (
          <SummaryRow label="First Status Date" value={fmtDate(values.loan_first_status_date)} />
        )}
      </SummarySection>

      {hasPrincipalSchedule && (
        <SummarySection title="Principal Repayment">
          <SummaryRow label="Frequency" value={fmtFreq(values.principal_frequency)} />
          <SummaryRow
            label="Reference Index"
            value={values.principal_reference_index ?? "None (fixed)"}
          />
          {values.principal_reference_index && (
            <SummaryRow label="Link Percentage" value={fmtRate(values.principal_link_percent)} />
          )}
          {isSimpleLoan && values.principal_first_payment_date && (
            <SummaryRow label="First Payment Date" value={fmtDate(values.principal_first_payment_date)} />
          )}
        </SummarySection>
      )}

      {hasRegularInterest && (
        <SummarySection title="Regular Interest">
          <SummaryRow label="Base Rate" value={fmtRate(values.base_rate)} />
          <SummaryRow label="Rate Structure" value={fmtFloat(values.float_type)} />
          {values.interest_reference_index && (
            <SummaryRow label="Reference Index" value={values.interest_reference_index} />
          )}
          {values.float_type === "spread" && (
            <SummaryRow label="Rate Fixing" value={values.interest_rate_fixing} />
          )}
          {values.float_type === "linked" && (
            <SummaryRow label="Link Percentage" value={fmtRate(values.interest_link_percent)} />
          )}
          <SummaryRow label="Payment Frequency" value={fmtFreq(values.interest_frequency)} />
          <SummaryRow label="Day-Count Convention" value={values.day_count_convention} />
          {values.floor_rate != null && (
            <SummaryRow label="Floor Rate" value={fmtRate(values.floor_rate)} />
          )}
          {values.ceiling_rate != null && (
            <SummaryRow label="Ceiling Rate" value={fmtRate(values.ceiling_rate)} />
          )}
          {isSimpleLoan && values.interest_first_payment_date && (
            <SummaryRow label="First Payment Date" value={fmtDate(values.interest_first_payment_date)} />
          )}
          {isSimpleLoan && values.interest_reference_day && (
            <SummaryRow label="Reference Date" value={fmtDate(values.interest_reference_day)} />
          )}
          {isSimpleLoan && values.interest_reference_day_offset != null && (
            <SummaryRow label="Reference Day Offset" value={values.interest_reference_day_offset} />
          )}
        </SummarySection>
      )}

      {hasPIK && (
        <SummarySection title="PIK Interest">
          <SummaryRow label="PIK Base Rate" value={fmtRate(values.pik_base_rate)} />
          <SummaryRow label="Rate Structure" value={fmtFloat(values.pik_float_type)} />
          {values.pik_reference_index && (
            <SummaryRow label="Reference Index" value={values.pik_reference_index} />
          )}
          <SummaryRow
            label="Capitalization Frequency"
            value={fmtFreq(values.pik_capitalization_frequency)}
          />
          {!hasRegularInterest && (
            <SummaryRow label="Day-Count Convention" value={values.day_count_convention} />
          )}
          {isSimpleLoan && values.pik_first_capitalization_date && (
            <SummaryRow label="First Capitalization Date" value={fmtDate(values.pik_first_capitalization_date)} />
          )}
          {isSimpleLoan && values.pik_reference_day && (
            <SummaryRow label="Reference Date" value={fmtDate(values.pik_reference_day)} />
          )}
          {isSimpleLoan && values.pik_reference_day_offset != null && (
            <SummaryRow label="Reference Day Offset" value={values.pik_reference_day_offset} />
          )}
        </SummarySection>
      )}

      {values.fees.length > 0 && (
        <SummarySection title="Fees">
          {values.fees.map((fee, i) => (
            <SummaryRow
              key={i}
              label={fee.type.replace(/_/g, " ")}
              value={
                fee.percentage_of
                  ? `${fee.amount}% of ${fee.percentage_of.replace(/_/g, " ")}`
                  : `${currency} ${fee.amount?.toLocaleString()}`
              }
            />
          ))}
        </SummarySection>
      )}

      {facilityCreated && (
        <Box
          sx={{
            mt: 3,
            p: 3,
            borderRadius: 2,
            border: "1px solid",
            borderColor: "success.main",
            display: "flex",
            alignItems: "center",
            gap: 2
          }}
        >
          <CheckCircleIcon sx={{ color: "success.main" }} />
          <Typography variant="body2" color="success.dark" fontWeight={600}>
            Facility created successfully
          </Typography>
        </Box>
      )}
      {serverError && (
        <Box sx={{ mt: 3, p: 3, borderRadius: 2, border: "1px solid", borderColor: "error.main", bgcolor: "error.50" }}>
          <Typography variant="body2" color="error.main" fontWeight={600} gutterBottom>
            Validation error
          </Typography>
          <Box component="ul" sx={{ m: 0, pl: 2.5 }}>
            {serverError.map((msg, i) => (
              <Typography key={i} component="li" variant="body2" color="error.dark" sx={{ wordBreak: "break-word" }}>
                {msg}
              </Typography>
            ))}
          </Box>
        </Box>
      )}
    </Box>
  );

  // ── Step 7: First Drawdown ───────────────────────────────────────────────

  const renderStep7 = () => {
    const showStatusDate = Boolean(values.loan_status_frequency);

    return (
      <Box>
        <QuestionSlot visible label="What is the name of this drawdown? (optional)">
          <TextField
            fullWidth
            label="Drawdown Name or Tranche"
            value={wv.deal_title}
            onChange={(e) => setWv((p) => ({ ...p, deal_title: e.target.value }))}
            placeholder="e.g. Tranche A, Initial Drawdown"
          />
        </QuestionSlot>

        <QuestionSlot visible label="How much is being drawn down?">
          <LoanAmount
            formik={{
              values: { loan_amount: wv.loan_amount } as FormValues,
              touched: {},
              errors: {},
              setFieldValue: (_field: string, val: number) => {
                setWv((p) => ({ ...p, loan_amount: val }));
              }
            } as unknown as FormikProps<FormValues>}
            currency={currency}
            label="Drawdown Amount"
          />
        </QuestionSlot>

        <QuestionSlot visible={wv.loan_amount > 0} label="When is this drawdown made?">
          <DesktopDatePicker
            format={dateFormat}
            views={["year", "month", "day"]}
            label="Drawdown Date"
            value={wv.start_date ? dayjs(wv.start_date) : null}
            onChange={(v) => {
              if (!v) return;
              const iso = toIsoMidday(v);
              setWv((p) => ({
                ...p,
                start_date: iso,
                // Auto-fill downstream dates from the drawdown date + facility frequencies
                ...(showStatusDate && values.loan_status_frequency
                  ? { loan_first_status_date: getEndOfPeriod(v, values.loan_status_frequency) }
                  : {}),
                ...(hasPrincipalSchedule
                  ? { principal_first_payment_date: getEndOfPeriod(v, values.principal_frequency) }
                  : {}),
                ...(hasRegularInterest
                  ? { interest_first_payment_date: getEndOfPeriod(v, values.interest_frequency) }
                  : {}),
                ...(hasPIK && values.pik_capitalization_frequency
                  ? {
                      pik_first_capitalization_date: getEndOfPeriod(
                        v,
                        values.pik_capitalization_frequency
                      )
                    }
                  : {})
              }));
            }}
            sx={{ width: "100%" }}
          />
        </QuestionSlot>

        {showStatusDate && (
          <QuestionSlot visible={wv.loan_amount > 0} label="First status reporting date">
            <DesktopDatePicker
              format={dateFormat}
              views={["year", "month", "day"]}
              label="First Status Date"
              value={wv.loan_first_status_date ? dayjs(wv.loan_first_status_date) : null}
              onChange={(v) =>
                setWv((p) => ({ ...p, loan_first_status_date: v ? toIsoMidday(v) : null }))
              }
              sx={{ width: "100%" }}
              slotProps={{
                textField: {
                  helperText: "Auto-calculated from drawdown date — adjust if needed"
                }
              }}
            />
          </QuestionSlot>
        )}

        {hasPrincipalSchedule && (
          <QuestionSlot visible={wv.loan_amount > 0} label="First principal payment date">
            <DesktopDatePicker
              format={dateFormat}
              views={["year", "month", "day"]}
              label="First Principal Payment"
              value={wv.principal_first_payment_date ? dayjs(wv.principal_first_payment_date) : null}
              onChange={(v) =>
                setWv((p) => ({ ...p, principal_first_payment_date: v ? toIsoMidday(v) : null }))
              }
              sx={{ width: "100%" }}
              slotProps={{
                textField: {
                  helperText: "Auto-calculated from drawdown date — adjust if needed"
                }
              }}
            />
          </QuestionSlot>
        )}

        {hasRegularInterest && (
          <QuestionSlot visible={wv.loan_amount > 0} label="First interest payment date">
            <DesktopDatePicker
              format={dateFormat}
              views={["year", "month", "day"]}
              label="First Interest Payment"
              value={wv.interest_first_payment_date ? dayjs(wv.interest_first_payment_date) : null}
              onChange={(v) =>
                setWv((p) => ({ ...p, interest_first_payment_date: v ? toIsoMidday(v) : null }))
              }
              sx={{ width: "100%" }}
              slotProps={{
                textField: {
                  helperText: "Auto-calculated from drawdown date — adjust if needed"
                }
              }}
            />
          </QuestionSlot>
        )}

        {hasPIK && (
          <QuestionSlot visible={wv.loan_amount > 0} label="First PIK capitalization date">
            <DesktopDatePicker
              format={dateFormat}
              views={["year", "month", "day"]}
              label="First PIK Capitalization"
              value={wv.pik_first_capitalization_date ? dayjs(wv.pik_first_capitalization_date) : null}
              onChange={(v) =>
                setWv((p) => ({ ...p, pik_first_capitalization_date: v ? toIsoMidday(v) : null }))
              }
              sx={{ width: "100%" }}
              slotProps={{
                textField: {
                  helperText: "Auto-calculated from drawdown date — adjust if needed"
                }
              }}
            />
          </QuestionSlot>
        )}
      </Box>
    );
  };

  // ── Step 8: Drawdown Review ──────────────────────────────────────────────

  const renderStep8 = () => (
    <Box>
      <SummarySection title="Drawdown Details">
        {wv.deal_title && <SummaryRow label="Name" value={wv.deal_title} />}
        <SummaryRow
          label="Amount"
          value={`${currency} ${wv.loan_amount?.toLocaleString()}`}
        />
        <SummaryRow label="Drawdown Date" value={fmtDate(wv.start_date)} />
      </SummarySection>

      {wv.loan_first_status_date && (
        <SummarySection title="Status Reporting">
          <SummaryRow label="First Status Date" value={fmtDate(wv.loan_first_status_date)} />
          <SummaryRow label="Frequency" value={fmtFreq(values.loan_status_frequency)} />
        </SummarySection>
      )}

      {(wv.principal_first_payment_date ||
        wv.interest_first_payment_date ||
        wv.pik_first_capitalization_date) && (
        <SummarySection title="First Payment Dates">
          {wv.principal_first_payment_date && (
            <SummaryRow
              label="First Principal Payment"
              value={fmtDate(wv.principal_first_payment_date)}
            />
          )}
          {wv.interest_first_payment_date && (
            <SummaryRow
              label="First Interest Payment"
              value={fmtDate(wv.interest_first_payment_date)}
            />
          )}
          {wv.pik_first_capitalization_date && (
            <SummaryRow
              label="First PIK Capitalization"
              value={fmtDate(wv.pik_first_capitalization_date)}
            />
          )}
        </SummarySection>
      )}

      <Box
        sx={{
          mt: 3,
          p: 3,
          borderRadius: 2,
          border: "1px solid",
          borderColor: "divider",
          bgcolor: "action.hover"
        }}
      >
        <Typography variant="body2" color="text.secondary">
          Interest rates, payment structure, and all other terms are inherited from the facility
          configuration. You can override individual terms later via the Edit screen.
        </Typography>
      </Box>
      {serverError && (
        <Box sx={{ mt: 3, p: 3, borderRadius: 2, border: "1px solid", borderColor: "error.main", bgcolor: "error.50" }}>
          <Typography variant="body2" color="error.main" fontWeight={600} gutterBottom>
            Validation error
          </Typography>
          <Box component="ul" sx={{ m: 0, pl: 2.5 }}>
            {serverError.map((msg, i) => (
              <Typography key={i} component="li" variant="body2" color="error.dark" sx={{ wordBreak: "break-word" }}>
                {msg}
              </Typography>
            ))}
          </Box>
        </Box>
      )}
    </Box>
  );

  // ── Step 9: Done ─────────────────────────────────────────────────────────

  const renderStep9 = () => (
    <Box sx={{ textAlign: "center", py: 6 }}>
      <CheckCircleIcon sx={{ fontSize: 72, color: "success.main", mb: 3 }} />
      <Typography variant="h5" fontWeight={700} gutterBottom>
        {isSimpleLoan ? "Loan Created!" : "Credit Facility Created!"}
      </Typography>
      <Typography variant="body1" color="text.secondary" sx={{ mb: 5, maxWidth: 400, mx: "auto" }}>
        {values.is_facility
          ? "Your credit facility has been successfully configured. You can manage drawdowns, payments, and terms from the facility screen."
          : "Your loan has been successfully configured. You can track payments and manage terms from the loan screen."}
      </Typography>
      <Button variant="contained" size="lg" onClick={() => onClose(true)}>
        Go to Facility
      </Button>
    </Box>
  );

  // ── Dispatch step content ─────────────────────────────────────────────────

  const renderStepContent = () => {
    switch (step) {
      case 0: return renderStep0();
      case 1: return renderStep1();
      case 2: return renderStep2();
      case 3: return renderStep3();
      case 4: return renderStep4();
      case 5: return renderStep5();
      case 6: return renderStep6();
      case 7: return renderStep7();
      case 8: return renderStep8();
      case 9: return renderStep9();
      default: return null;
    }
  };

  // ── Footer logic ──────────────────────────────────────────────────────────

  const isLastStep = step === 9;
  const facilityAlreadyCreated = step === 6 && facilityCreated;

  const getNextLabel = (): string | null => {
    if (isLastStep) return null;
    if (step === 6) return facilityAlreadyCreated ? "Continue" : submitting ? "Creating…" : isSimpleLoan ? "Create Loan" : "Create Facility";
    if (step === 8) return submitting ? "Creating…" : "Create Drawdown";
    return "Continue";
  };

  const handleNextClick = () => {
    if (step === 6 && !facilityAlreadyCreated) {
      handleFacilityConfirm();
    } else if (step === 8) {
      handleWithdrawalConfirm();
    } else {
      goNext();
    }
  };

  const nextLabel = getNextLabel();
  const backDisabled = currentStepIndex === 0 || (facilityCreated && step === 6);

  const isNextDisabled = (() => {
    switch (step) {
      case 0:
        return (
          !debtTypeChosen ||
          values.loan_amount <= 0 ||
          !values.start_date ||
          !values.maturity_date
        );
      case 1:
        return (
          principalLinked === null ||
          (principalLinked && !values.principal_reference_index)
        );
      case 2:
        return interestType === null;
      case 3:
        return (
          (hasRegularInterest && values.base_rate == null) ||
          (hasRegularInterest && values.float_type !== "fixed" && !values.interest_reference_index) ||
          (hasRegularInterest && values.float_type === "spread" && !values.interest_rate_fixing)
        );
      case 4:
        return (
          values.pik_base_rate == null ||
          (values.pik_float_type !== "fixed" && !values.pik_reference_index)
        );
      case 5:
        return hasFees === null;
      case 7:
        return wv.loan_amount <= 0 || !wv.start_date;
      default:
        return false;
    }
  })();

  const { title, subtitle } = STEP_META[step];

  // ── Render ────────────────────────────────────────────────────────────────

  return (
    <>
    <Modal
      open={open}
      onClose={() => {}}
      sx={{ display: "flex", alignItems: "center", justifyContent: "center" }}
    >
      <Paper
        sx={{
          width: "min(680px, 94vw)",
          height: "min(780px, 92vh)",
          display: "flex",
          flexDirection: "column",
          overflow: "hidden",
          borderRadius: 4
        }}
      >
        {/* ── Header ─────────────────────────────────────────────────── */}
        <Box sx={{ px: 5, pt: 5, pb: 3, borderBottom: "1px solid", borderColor: "divider" }}>
          <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
            <Box>
              <Typography variant="h6" fontWeight={700}>
                {title}
              </Typography>
              {subtitle && (
                <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                  {subtitle}
                </Typography>
              )}
            </Box>
            {!isLastStep && (
              <IconButton onClick={() => onClose(facilityCreated)} size="sm" sx={{ mt: -0.5 }}>
                <CloseIcon />
              </IconButton>
            )}
          </Box>

          {/* Progress bar */}
          <Box sx={{ mt: 3 }}>
            <Box sx={{ display: "flex", justifyContent: "space-between", mb: 1 }}>
              <Typography variant="caption" color="text.secondary">
                Step {currentStepIndex + 1} of {totalSteps}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {Math.round(progressPct)}% complete
              </Typography>
            </Box>
            <LinearProgress variant="determinate" value={progressPct} sx={{ height: 5, borderRadius: 3 }} />
          </Box>
        </Box>

        {/* ── Scrollable content ──────────────────────────────────────── */}
        <Box sx={{ flex: 1, overflow: "auto", px: 5, py: 4 }}>
          {renderStepContent()}
        </Box>

        {/* ── Footer ─────────────────────────────────────────────────── */}
        {!isLastStep && (
          <Box
            sx={{
              px: 5,
              py: 3,
              borderTop: "1px solid",
              borderColor: "divider",
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center"
            }}
          >
            {!backDisabled ? (
              <Button
                startIcon={<ArrowBackIcon />}
                onClick={goBack}
                variant="text"
                color="inherit"
              >
                Back
              </Button>
            ) : (
              <Box />
            )}

            <Box sx={{ display: "flex", gap: 2, alignItems: "center" }}>
              {/* "Skip drawdown" option on the drawdown config step */}
              {step === 7 && (
                <Button variant="text" color="inherit" onClick={() => setStep(9)}>
                  Skip for now
                </Button>
              )}

              {nextLabel && (
                <Button
                  variant="contained"
                  onClick={handleNextClick}
                  disabled={submitting || loading || isNextDisabled}
                  sx={{ minWidth: 160 }}
                >
                  {nextLabel}
                </Button>
              )}
            </Box>
          </Box>
        )}
      </Paper>
    </Modal>

    {rounds && wizardSegments && (
      <FacilityTermsDrawerValues
        open={!!wizardSegments}
        rounds={rounds}
        loading={measureDataLoading || roundsLoading}
        segments={wizardSegments}
        onClose={() => { setWizardSegments(null); goNext(); }}
        onSave={handleSaveMeasureData}
        onSaveAsDraft={handleSaveMeasureDataAsDraft}
        onSkip={() => { setWizardSegments(null); goNext(); }}
      />
    )}
    </>
  );
};
